/**
 * Match Detail Desktop Component
 * Desktop layout with live stream and score card
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useBetting } from '@/Context/BettingContext';
import SportsBetSlip from '../SportsBetSlip/SportsBetSlip';
import MatchOdds from './MatchOdds';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import styles from './MatchDetailDesktop.module.css';

// Sport images for carousel (JSON structure)
const SPORT_CAROUSEL_IMAGES = [
  '/assets/images/first.webp',
  '/assets/images/second.webp',
  '/assets/images/third.webp'
];

const MatchDetailDesktop = ({ matchData }) => {
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const { selectedBets } = useBetting();
  const [showStream, setShowStream] = useState(true);
  const betSlipRef = useRef(null);

  // Auto-scroll to betslip when bet is added
  useEffect(() => {
    if (selectedBets.length > 0 && betSlipRef.current) {
      betSlipRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedBets.length]);

  return (
    <div className={styles.matchDetailDesktop}>
      {/* Header with Banner Carousel - Full Width */}
      <div className={styles.header}>
        <Swiper
          modules={[Autoplay]}
          spaceBetween={16}
          slidesPerView={3}
          slidesPerGroup={1}
          navigation={false}
          pagination={false}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
          }}
          loop={true}
          speed={800}
          breakpoints={{
            320: {
              slidesPerView: 1,
              slidesPerGroup: 1,
              spaceBetween: 8,
            },
            768: {
              slidesPerView: 2,
              slidesPerGroup: 1,
              spaceBetween: 12,
            },
            1024: {
              slidesPerView: 3,
              slidesPerGroup: 1,
              spaceBetween: 16,
            },
          }}
          className={styles.bannerCarousel}
        >
          {SPORT_CAROUSEL_IMAGES.map((image, index) => (
            <SwiperSlide key={index} className={styles.carouselSlide}>
              <img 
                src={image}
                alt={`Sports Banner ${index + 1}`}
                className={styles.bannerImage}
                onError={(e) => {
                  e.target.src = '/assets/images/sports-bg.webp';
                }}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <div className={styles.bannerOverlay}></div>
      </div>

      {/* Main Content - Flex Row Layout */}
      <div className={styles.contentRow}>
        {/* Left Side - Match Odds */}
        <div className={styles.leftContent}>
          <div className={styles.bettingSection}>
            <h3 className={styles.sectionTitle}>Match Odds</h3>
            <MatchOdds matchData={matchData} />
          </div>
        </div>

        {/* Right Side - Live Match & Bet Slip */}
        <div className={styles.rightContent}>
          {/* Live Stream */}
          {matchData?.eventid && (
            <div className={styles.liveStreamSection}>
              <div className={styles.streamHeader}>
                <span>Live Match</span>
                <button 
                  className={styles.playButton}
                  onClick={() => setShowStream(!showStream)}
                >
                  {showStream ? 'Stop' : 'Play'}
                </button>
              </div>
              {showStream && (
                <div className={styles.streamContainer}>
                  <iframe
                    src={`https://live-stream.softgamingapi.com/?eventid=${matchData.eventid}`}
                    title="Live Stream"
                    className={styles.streamFrame}
                    allowFullScreen
                  />
                </div>
              )}
            </div>
          )}

          {/* Score Card */}
          {matchData?.eventid && (
            <div className={styles.scoreCardSection}>
              <iframe
                src={`https://betfair-score.softgamingapi.com/sport-radar-ui?event_id=${matchData.eventid}`}
                title="Live Score"
                className={styles.scoreFrame}
              />
            </div>
          )}

          {/* Bet Slip */}
          <div className={styles.betSlipSection} ref={betSlipRef}>
            <SportsBetSlip />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchDetailDesktop;
